import { useEffect, useRef, useState } from 'react'
import { useAuth } from '../../context/AuthContext'
import { renewalStatus, requestRenewal } from '../../services/renewal'
import { FEATURES, type AdminUser } from '../../types'
import { PLANS, PERIOD_LABEL, PRICES_URL } from '../../plans'
import { ContactPhoneSetting } from '../ContactPhoneSetting'
import { DeleteAccount } from './DeleteAccount'
import { LegalLinks } from '../LegalLinks'
import { isViewMode } from '../../services/viewMode'
import { removeTenantLogo, uploadTenantLogo } from '../../services/tenantLogo'
import { ClipboardListIcon, ImageIcon, MailIcon, PencilIcon, StoreIcon, TrashIcon, UserIcon } from '../icons'
import './AccountPanel.css'

const DAY = 86_400_000
/** Kayıtta verilen deneme süresi (supabase/functions/activate ile aynı). */
const TRIAL_DAYS = 14

/** Deneme durumu, ayarların başlığında kısa yazı için de kullanılıyor. */
export function trialSummary(trialEndsAt: number | null, now = Date.now()): string {
  if (trialEndsAt === null) return 'Süresiz kullanım'
  const left = trialEndsAt - now
  if (left <= 0) return 'Deneme süresi doldu'
  const days = Math.ceil(left / DAY)
  return days <= 1 ? 'Deneme bugün bitiyor' : `Deneme: ${days} gün kaldı`
}

interface Props {
  /** Kurumun bütün kullanıcıları (patron dahil); personel sayısı patronsuz. */
  staff: AdminUser[]
}

/**
 * Ayarlar > Hesabım: işletmenin kimliği, aboneliği (paket, deneme süresinin ne
 * zaman bittiği, devam talebi) ve işletme bilgileri tek panelde (kullanıcı,
 * 6 Eki 2026; eskiden ayrı "Kurum" bölümü vardı). Son 3 günde her sayfanın
 * üstünde ayrıca uyarı çıkıyor (TrialBanner).
 */
export function AccountPanel({ staff }: Props) {
  const { tenantId, tenantName, tenantLogo, trialEndsAt, plan, planPeriod, staffLimit, features, admin } = useAuth()
  // Logo (2 Kas 2026): yalnız patron yükler, görüntüleme modunda değil.
  const canLogo = admin?.role === 'owner' && !isViewMode()
  const logoInput = useRef<HTMLInputElement>(null)
  const [logoBusy, setLogoBusy] = useState(false)
  const [logoError, setLogoError] = useState<string | null>(null)
  const pickLogo = () => logoInput.current?.click()
  const onLogoFile = async (file: File | undefined) => {
    if (!file || !tenantId) return
    setLogoBusy(true)
    setLogoError(null)
    try {
      await uploadTenantLogo(tenantId, file, tenantLogo)
    } catch (e) {
      setLogoError(e instanceof Error ? e.message : 'Logo yüklenemedi.')
    } finally {
      setLogoBusy(false)
      if (logoInput.current) logoInput.current.value = ''
    }
  }
  const dropLogo = async () => {
    if (!tenantId) return
    setLogoBusy(true)
    setLogoError(null)
    try {
      await removeTenantLogo(tenantId, tenantLogo)
    } catch (e) {
      setLogoError(e instanceof Error ? e.message : 'Logo kaldırılamadı.')
    } finally {
      setLogoBusy(false)
    }
  }
  const now = Date.now()
  const onTrial = trialEndsAt !== null
  const expired = onTrial && now >= trialEndsAt
  const left = onTrial ? trialEndsAt - now : 0
  const days = Math.ceil(left / DAY)
  const used = onTrial ? Math.min(1, Math.max(0, 1 - left / (TRIAL_DAYS * DAY))) : 0

  const endText = onTrial
    ? new Date(trialEndsAt).toLocaleString('tr-TR', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
        weekday: 'long',
        hour: '2-digit',
        minute: '2-digit',
      })
    : ''

  /** Bekleyen devam talebinin tarihi; undefined henüz bilinmiyor. */
  const [pending, setPending] = useState<string | null | undefined>(undefined)
  const [sending, setSending] = useState(false)
  const [sendError, setSendError] = useState<string | null>(null)

  // Bir üst paket (yükseltme talebi); denemede ve Max'ta yok.
  const next = plan ? PLANS[PLANS.findIndex((p) => p.id === plan.id) + 1] ?? null : null
  const asks = onTrial || plan !== null

  useEffect(() => {
    if (!tenantId || !asks) return
    let alive = true
    renewalStatus(tenantId)
      .then((d) => alive && setPending(d))
      .catch(() => alive && setPending(null))
    return () => {
      alive = false
    }
  }, [tenantId, asks])

  const send = async () => {
    if (!tenantId) return
    setSending(true)
    setSendError(null)
    try {
      setPending(await requestRenewal(tenantId, onTrial ? undefined : next?.id))
    } catch (err) {
      setSendError(err instanceof Error ? err.message : 'Gönderilemedi.')
    } finally {
      setSending(false)
    }
  }

  const modules = FEATURES.filter((f) => features[f.key] === true).map((f) => f.name)
  const planName = plan ? plan.name : null
  const owner = staff.find((s) => s.role === 'owner')
  // Ayarlar > Kullanıcılar ile aynı sayı: patron hariç (kullanıcı, 13 Eki 2026).
  const team = staff.filter((s) => s.role !== 'owner')
  const cashiers = team.filter((s) => s.role === 'cashier').length
  const waiters = team.filter((s) => s.role === 'waiter').length
  const kitchen = team.filter((s) => s.role === 'kitchen').length
  // Paketin personel sınırına sayılanlar: kasa ve garson (plans.ts).
  const counted = cashiers + waiters
  // Personelin sentetik adresi gösterilmiyor, yalnız gerçek e-posta.
  const loginEmail =
    admin?.email && !/@kullanici\.adissoo\.com$/.test(admin.email) ? admin.email : null

  const state = expired ? 'is-over' : onTrial ? 'is-trial' : 'is-active'

  return (
    <div className="acct">
      {/* Kimlik: kim olduğu ve hesabın durumu tek bakışta. */}
      <header className={`acct__id ${state}`}>
        {/* Logo kutusu: logo varsa o, yoksa dükkan simgesi. Patron basınca
            logo seçer (kullanıcı: "hesabımdan yüklenebilir ikon"). */}
        {canLogo ? (
          <button
            type="button"
            className={`acct__logo acct__logo--btn ${tenantLogo ? 'has-img' : ''}`}
            onClick={pickLogo}
            disabled={logoBusy}
            aria-label={tenantLogo ? 'Logoyu değiştir' : 'Logo yükle'}
            title={tenantLogo ? 'Logoyu değiştir' : 'Logo yükle'}
          >
            {tenantLogo ? <img src={tenantLogo} alt="" /> : <StoreIcon />}
            <span className="acct__logo-edit" aria-hidden="true">
              <PencilIcon />
            </span>
          </button>
        ) : (
          <span className={`acct__logo ${tenantLogo ? 'has-img' : ''}`} aria-hidden="true">
            {tenantLogo ? <img src={tenantLogo} alt="" /> : <StoreIcon />}
          </span>
        )}
        <input
          ref={logoInput}
          type="file"
          accept="image/png,image/jpeg,image/webp"
          hidden
          onChange={(e) => void onLogoFile(e.target.files?.[0])}
        />
        <div className="acct__id-text">
          {/* Abonelik tipi adın yanında (kullanıcı, 13 Eki 2026). */}
          <h3>
            {tenantName}
            <span className={`acct__type ${state}`}>
              {onTrial ? trialSummary(trialEndsAt, now) : planName ? `${planName} paketi` : 'Süresiz kullanım'}
            </span>
          </h3>
          <span>
            Kurum kodu <code>{tenantId}</code>
          </span>
        </div>
        <span className="acct__badge">{expired ? 'Deneme bitti' : onTrial ? 'Deneme sürümü' : 'Aktif'}</span>
      </header>

      <div className="acct__grid acct__grid--stack">
        {/* Abonelik kutusu denemede (süre çubuğu, devam talebi) ve paketli
            hesapta (paket, personel sınırı, yükseltme talebi). Paketsiz
            süresiz hesapta tipi zaten adın yanında yazıyor. */}
        {asks && (
        <section className={`acct__box acct__sub ${state}`} aria-label="Abonelik">
          <h4>Abonelik</h4>
          {onTrial ? (
            <>
              <strong className="acct__big">
                {expired ? 'Deneme süreniz doldu' : days <= 1 ? 'Bugün son gün' : `${days} gün kaldı`}
              </strong>
              <span className="acct__when">
                {expired ? `${endText} itibarıyla bitti.` : `${endText} tarihinde bitiyor.`}
              </span>
              <div
                className="acct__bar"
                role="progressbar"
                aria-label="Deneme süresi"
                aria-valuemin={0}
                aria-valuemax={TRIAL_DAYS}
                aria-valuenow={Math.round(used * TRIAL_DAYS)}
              >
                <i style={{ width: `${used * 100}%` }} />
              </div>
              <p className="acct__note">
                {expired
                  ? 'Kasa yeni sipariş almıyor. Menünüz, satışlarınız ve raporlarınız silinmedi; devam ettiğinizde kaldığınız yerden sürer.'
                  : 'Süre dolunca kasa yeni sipariş almaz, verileriniz silinmez.'}
              </p>
            </>
          ) : (
            <>
              <strong className="acct__big">
                {planName} paketi
                {planPeriod && <span className="acct__period">{PERIOD_LABEL[planPeriod]}</span>}
              </strong>
              <span className="acct__when">
                {staffLimit === null
                  ? `Sınırsız personel, şu an ${counted} kasa ve garson`
                  : `${counted} / ${staffLimit} kasa ve garson`}
              </span>
              {staffLimit !== null && (
                <div
                  className={`acct__bar ${counted >= staffLimit ? 'is-full' : ''}`}
                  role="progressbar"
                  aria-label="Personel sınırı"
                  aria-valuemin={0}
                  aria-valuemax={staffLimit}
                  aria-valuenow={counted}
                >
                  <i style={{ width: `${Math.min(1, counted / staffLimit) * 100}%` }} />
                </div>
              )}
              <p className="acct__note">Patron ve mutfak ekranı hesapları personel sınırına sayılmaz.</p>
            </>
          )}

          {/* Fiyat burada yazmıyor, sitede (29 Eki 2026). iOS dalında
              isNativeApp() ile gizle (Apple 3.1.1). */}
          <div className="acct__plan">
            <span className="acct__plan-label">{onTrial ? 'Deneme süresince' : 'Paketler'}</span>
            <div className="acct__plan-row">
              <span>{onTrial ? 'Bütün özellikler açık' : 'Start, Plus ve Max karşılaştırması'}</span>
              <a className="acct__plan-link" href={PRICES_URL} target="_blank" rel="noopener">
                Paketler ve fiyatlar
              </a>
            </div>
          </div>

          {/* Bekleyen talep var mı bilinene kadar boş: önce soluk düğme çıkıp
              sonra yeşil kutuya dönmesi yeni talep gitmiş gibi görünüyordu. */}
          {(onTrial || next) &&
            pending !== undefined &&
            (pending ? (
              <div className="acct__sent" role="status">
                <strong>Talebiniz alındı</strong>
                <span>
                  {new Date(pending).toLocaleString('tr-TR', {
                    day: 'numeric',
                    month: 'long',
                    hour: '2-digit',
                    minute: '2-digit',
                  })}{' '}
                  tarihinde gönderdiniz. En kısa sürede size ulaşacağız.
                </span>
              </div>
            ) : (
              <div className="acct__ask">
                <button type="button" className="btn btn--primary" onClick={() => void send()} disabled={sending}>
                  {sending
                    ? 'Gönderiliyor…'
                    : !onTrial && next
                      ? `${next.name} paketine geçmek istiyorum`
                      : expired
                        ? 'Devam etmek istiyorum'
                        : 'Paketle devam etmek istiyorum'}
                </button>
                <span className="acct__ask-hint">
                  {!onTrial && next
                    ? 'Talebiniz bize ulaşır, sizi arayıp paket değişikliği için bilgi veririz.'
                    : 'Talebiniz bize ulaşır, sizi arayıp ödeme ve devam için bilgi veririz.'}
                </span>
                {sendError && <span className="acct__err">{sendError}</span>}
              </div>
            ))}
        </section>
        )}

        {/* İşletme bilgileri: tam genişlik, her bilgi simgeli bir satır
            (kullanıcı, 13 Eki 2026). */}
        <section className="acct__box acct__info" aria-label="İşletme bilgileri">
          <h4>İşletme bilgileri</h4>

          <div className="ainfo">
            <InfoItem icon={<UserIcon />} label="Yönetici">
              {owner ? owner.displayName || owner.username : <span className="setting__muted">atanmamış</span>}
            </InfoItem>

            {loginEmail && (
              <InfoItem icon={<MailIcon />} label="Giriş e-postası">
                <span className="ainfo__email">{loginEmail}</span>
              </InfoItem>
            )}

            <ContactPhoneSetting />

            {canLogo && (
              <InfoItem
                icon={<ImageIcon />}
                label="Logo"
                action={
                  <span className="ainfo__acts">
                    {tenantLogo && (
                      <button
                        type="button"
                        className="icon-btn"
                        onClick={() => void dropLogo()}
                        disabled={logoBusy}
                        title="Kaldır"
                        aria-label="Logoyu kaldır"
                      >
                        <TrashIcon />
                      </button>
                    )}
                    <button
                      type="button"
                      className="icon-btn"
                      onClick={pickLogo}
                      disabled={logoBusy}
                      title={tenantLogo ? 'Değiştir' : 'Yükle'}
                      aria-label={tenantLogo ? 'Logoyu değiştir' : 'Logo yükle'}
                    >
                      <PencilIcon />
                    </button>
                  </span>
                }
              >
                {logoBusy ? (
                  <span className="setting__muted">Yükleniyor…</span>
                ) : tenantLogo ? (
                  'Yüklü, sol üstte görünüyor'
                ) : (
                  <span className="setting__muted">yüklenmemiş, Adissoo işareti görünüyor</span>
                )}
                {logoError && <span className="ainfo__error">{logoError}</span>}
              </InfoItem>
            )}

            <InfoItem icon={<ClipboardListIcon />} label="Personel">
              {team.length} kişi
              {team.length > 0 && (
                <span className="setting__muted">
                  {', '}
                  {[
                    cashiers && `${cashiers} kasa`,
                    waiters && `${waiters} garson`,
                    kitchen && `${kitchen} mutfak`,
                  ]
                    .filter(Boolean)
                    .join(', ')}
                </span>
              )}
            </InfoItem>
          </div>

          <div className="ainfo__mods">
            <span className="ainfo__label">Açık modüller</span>
            {modules.length ? (
              <span className="acct__chips">
                {modules.map((m) => (
                  <span key={m}>{m}</span>
                ))}
              </span>
            ) : (
              <span className="setting__muted">yok</span>
            )}
          </div>
        </section>

        {/* Yalnız patron, kendi işletmesini (App Store 5.1.1(v)). Sistem
            yöneticisinin salt okunur görüntülemesinde yok. */}
        {admin?.role === 'owner' && !isViewMode() && <DeleteAccount />}
      </div>

      <LegalLinks className="acct__legal" />
    </div>
  )
}

/** İşletme bilgileri satırı: simge, küçük etiket, değer. */
export function InfoItem({
  icon,
  label,
  children,
  action,
}: {
  icon: React.ReactNode
  label: string
  children: React.ReactNode
  action?: React.ReactNode
}) {
  return (
    <div className="ainfo__item">
      <span className="ainfo__icon" aria-hidden="true">
        {icon}
      </span>
      <div className="ainfo__body">
        <span className="ainfo__label">{label}</span>
        <div className="ainfo__value">{children}</div>
      </div>
      {action}
    </div>
  )
}
